<template>
    <div class="modal-backdrop" @click="handleBackdropClick">
        <div class="modal-container">
            <div class="button-group">
                <button @click="downloadImage" class="icon-button" aria-label="Download">
                    <PhDownloadSimple :size="20" />
                </button>
                <button @click="closeModal" class="icon-button" aria-label="Close">
                    <XMarkIcon class="h-5 w-5" />
                </button>
            </div>
            <div class="image-wrapper" @click.stop>
                <img :src="dialogRef.data.imageUrl" alt="snapshot" class="image-content" />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { XMarkIcon } from '@heroicons/vue/24/outline';
import { ModalNames } from '~/types/modals';

const dialogRef = getDialogRef(ModalNames.IMAGE_VIEWER);

const closeModal = () => {
    dialogRef.value.close();
};

const handleBackdropClick = (event: MouseEvent) => {
    // Close if clicking on the backdrop (not on the image container)
    if (event.target === event.currentTarget) {
        closeModal();
    }
};

function getFileName(url: string): string {
    try {
        const parts = url.split('/');
        return parts[parts.length - 1] || 'image.jpg';
    } catch {
        return 'image.jpg';
    }
}

async function downloadImage() {
    const url = dialogRef.value.data.imageUrl;
    
    // First, try direct download with anchor tag (simplest approach)
    try {
        const link = document.createElement('a');
        link.href = url;
        link.download = getFileName(url);
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Give it a moment, if it didn't work, try fetch approach
        setTimeout(() => {
            // If direct download didn't work, try fetch
            fetch(url)
                .then(response => {
                    if (!response.ok) throw new Error('Failed to fetch');
                    return response.blob();
                })
                .then(blob => {
                    const blobUrl = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.download = getFileName(url);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(blobUrl);
                })
                .catch(() => {
                    // Final fallback: open in new tab for user to manually save
                    window.open(url, '_blank');
                });
        }, 100);
    } catch (e) {
        // If everything fails, open in new tab
        window.open(url, '_blank');
    }
}
</script>

<style lang="scss" scoped>
.modal-backdrop {
    @apply fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50;
}

.modal-container {
    @apply relative inline-block;
}

.button-group {
    @apply absolute -top-8 right-0 flex gap-2 z-10;
}

.icon-button {
    @apply p-2 text-white cursor-pointer transition-opacity;
    background: transparent;
    border: none;
    
    &:hover {
        opacity: 0.8;
    }
}

.image-wrapper {
    @apply relative inline-block max-w-[90vw] max-h-[90vh] bg-white rounded-lg;
    
    .image-content {
        @apply max-w-full max-h-[90vh] w-full h-auto object-contain block rounded-lg;
    }
}
</style>
